import { Box, Button, Menu, styled, Typography, useTheme, TextField } from "@mui/material";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

// Security
// import { checkRateLimit, getRateLimitRemainingTime } from "../../utils/security";
import { renderErrorMessage } from "../../lib/toastMessage";
import { RHFTextField } from "src/components/hook-form";

const RowBox = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: "10px",
  width: "100%",
}));

const RowInnerBox = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  // justifyContent: 'space-between',
  width: "calc(100%/2 - 5px)",
  gap: "5px",
}));

const Key = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.grey[400],
}));

const ITEM_HEIGHT = 38;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: "auto",
    },
  },
};

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
  };
}

const FilterMenu = ({ anchorEl, open, handleClose, reset, apply, isFilterApplied }) => {
  const theme = useTheme();
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [status, setStatus] = useState([]);
  const [method, setMethod] = useState([]);

  const handleChangeStatus = (event) => {
    const {
      target: { value },
    } = event;
    setStatus(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleChangeMethod = (event) => {
    const {
      target: { value },
    } = event;
    setMethod(value);
  };

  useEffect(() => {
    if (!isFilterApplied) {
      setFrom(null);
      setTo(null);
      setInvoiceId("");
      setPaymentId("");
      setStatus([]);
      setMethod([]);
    }
  }, [isFilterApplied]);

  const resetFilter = () => {
    setFrom(null);
    setTo(null);
    setInvoiceId("");
    setPaymentId("");
    setStatus([]);
    setMethod([]);
    reset();
  };

  const applyFilter = () => {
    // Rate limiting check
    // if (!checkRateLimit("invoiceFilter", 5, 60000)) {
    //   const remainingTime = getRateLimitRemainingTime("invoiceFilter");
    //   renderErrorMessage(`Too many filter attempts. Please try again in ${remainingTime}.`);
    //   return;
    // }

    const data = {
      paymentId,
      invoiceId,
      status,
      method,
    };

    if (from) {
      data.from = Number(new Date(from));
    }
    if (to) {
      data.to = Number(new Date(to));
    }

    apply(data);
  };

  return (
    <Menu
      id="filter-button"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{
        "aria-labelledby": "filter-button",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          gap: "15px",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <RowBox>
            <RowInnerBox>
              <Key>From</Key>

              <DatePicker slotProps={{ textField: { size: "small" } }} value={from} onChange={(e) => setFrom(e)} />
            </RowInnerBox>

            <RowInnerBox>
              <Key>To</Key>

              <DatePicker slotProps={{ textField: { size: "small" } }} value={to} onChange={(e) => setTo(e)} />
            </RowInnerBox>
          </RowBox>
        </LocalizationProvider>

        <RowBox>
          <RowInnerBox>
            <Key>Invoice ID</Key>

            <TextField
              name="invoiceId"
              size="small"
              type="text"
              placeholder="Enter invoice ID"
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          </RowInnerBox>

          <RowInnerBox>
            <Key>Payment ID</Key>

            <TextField
              name="paymentId"
              size="small"
              type="text"
              placeholder="Enter Payment ID"
              value={paymentId}
              onChange={(e) => setPaymentId(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          </RowInnerBox>
        </RowBox>

        <RowBox>
          <RowInnerBox>
            <Key>Status</Key>

            <FormControl>
              <Select
                id="demo-multiple-chip"
                multiple
                size="small"
                value={status}
                onChange={handleChangeStatus}
                input={<OutlinedInput id="select-multiple-chip" placeholder="Select status" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value.charAt(0).toUpperCase() + value.slice(1)} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {["captured", "failed"].map((name) => (
                  <MenuItem key={name} value={name} style={getStyles(name, status, theme)}>
                    {name.charAt(0).toUpperCase() + name.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </RowInnerBox>

          <RowInnerBox>
            <Key>Method</Key>

            <FormControl>
              <Select
                id="demo-multiple-chip"
                multiple
                size="small"
                value={method}
                onChange={handleChangeMethod}
                input={<OutlinedInput id="select-multiple-chip" placeholder="Select status" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value.charAt(0).toUpperCase() + value.slice(1)} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {["card", "netbanking", "wallet", "emi", "upi"].map((name) => (
                  <MenuItem key={name} value={name} style={getStyles(name, status, theme)}>
                    {name.charAt(0).toUpperCase() + name.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </RowInnerBox>
        </RowBox>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
            gap: "8px",
            mt: "10px",
          }}
        >
          <Button variant="contained" sx={{ boxShadow: "none" }} onClick={() => applyFilter()}>
            Apply
          </Button>

          <Button variant="outlined" color="warning" sx={{ boxShadow: "none" }} onClick={() => resetFilter()}>
            Reset Filters
          </Button>

          <Button variant="outlined" sx={{ boxShadow: "none" }} onClick={() => handleClose()}>
            Close
          </Button>
        </Box>
      </Box>
    </Menu>
  );
};

FilterMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  reset: PropTypes.func,
  apply: PropTypes.func,
  isFilterApplied: PropTypes.bool,
};

export default FilterMenu;
